/**
 * FilesTabPane — the CardDetailDrawer's "Files" tab body. Surfaces the
 * manual upload section (QDrive-backed), the real downloadable email
 * files rail for email-backed cards, or the legacy attachment-id list +
 * empty state for manual cards.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * The caller still gates rendering on `safeActiveTab === 'files'`.
 */
import { Paperclip } from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter } from '../../../api'
import type { Card } from '../../../types'
import { CardFilesRail } from '../CardFilesRail'
import { CardAttachmentsSection } from '../CardAttachmentsSection'

export interface FilesTabPaneProps {
  card: Card
  api: KanbanApi
  emailAdapter?: KanbanEmailAdapter
}

export function FilesTabPane({ card, api, emailAdapter }: FilesTabPaneProps) {
  return (
    <div
      role="tabpanel"
      id="card-detail-tabpane-files"
      className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 pb-16 space-y-4"
    >
      {/* FEAT-2026-06-29-KANBAN-CARD-FILE-UPLOAD — net-new manual file
          upload/download/delete, tied to QDrive. Gated on the optional
          `uploadCardAttachment`. When present it OWNS the empty state.
          ARC-2 MR7: the adapter rides along so PDF/image uploads open
          IN-CARD (editor overlay + version rail), same as email chips. */}
      {api.uploadCardAttachment && (
        <CardAttachmentsSection card={card} api={api} emailAdapter={emailAdapter} />
      )}

      {/* FEAT-2026-06-23-CARD-FILES-RAIL — for email-backed cards render the
          REAL downloadable files via the same component the top rail uses.
          Manual cards keep the legacy id list + empty state below. */}
      {emailAdapter && card.primary_email ? (
        <CardFilesRail
          card={card}
          emailAdapter={emailAdapter}
          variant="panel"
          emptyState={!api.uploadCardAttachment}
        />
      ) : (() => {
        const attachmentIds = card.related_attachment_ids_json ?? []
        if (attachmentIds.length === 0) {
          // The upload section above already owns the empty state when it's
          // mounted — only show the legacy guidance on older hosts.
          if (api.uploadCardAttachment) return null
          return (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-neutral-500 dark:text-zinc-400">
              <Paperclip className="h-8 w-8 opacity-40" aria-hidden />
              <p className="text-sm font-medium">No files on this card yet</p>
              <p className="max-w-[280px] text-[11.5px] leading-snug">
                Email attachments allocated to this card show up here
                automatically. Open the Email tab to view inline
                attachments, or link an email to bring its files across.
              </p>
            </div>
          )
        }
        return (
          <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200 dark:divide-zinc-800 dark:border-zinc-800">
            {attachmentIds.map((id) => (
              <li
                key={String(id)}
                className="flex items-center justify-between gap-3 px-3 py-2 text-[12px] text-neutral-700 dark:text-zinc-200"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                  <span className="truncate font-mono text-[11px]">
                    attachment #{String(id)}
                  </span>
                </div>
                {emailAdapter?.buildEmailHubUrl && card.primary_email && (
                  <a
                    href={emailAdapter.buildEmailHubUrl(card.primary_email)}
                    className="rounded bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-700 hover:bg-neutral-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                  >
                    Open in Email Hub →
                  </a>
                )}
              </li>
            ))}
          </ul>
        )
      })()}
    </div>
  )
}
